import type { Stage } from '@api/types'
import { useI18n } from '@i18n/I18nProvider'
import { formatKm } from '@i18n/locale'
import { Icon } from '@ui/Icon'
import { type ReactNode, useEffect, useRef, useState } from 'react'

type Props = {
	stages: Stage[] // ya ordenadas
	selectedId: string | null
	onChoose: (id: string | null) => void
	children?: ReactNode // acciones de la etapa elegida (p.ej. el botón de sus detalles)
}

const ROW = 'flex min-h-14 w-full items-center gap-3 rounded-2xl px-3 py-2 text-left active:bg-earth-100'

// Botón "Etapas" del mapa y la lista para elegir una (o volver al Camino entero). Con una etapa
// elegida, el botón se convierte en una pastilla compacta con su número y nombre (marcada), y a
// su lado van sus acciones y una "x" para volver al Camino entero: el mapa queda despejado.
export const StagePicker = ({ stages, selectedId, onChoose, children }: Props) => {
	const { t, locale } = useI18n()
	const [open, setOpen] = useState(false)
	const closeButton = useRef<HTMLButtonElement>(null)
	// Al abrir la lista, el foco entra en ella (lectores de pantalla y teclado).
	useEffect(() => {
		if (open) closeButton.current?.focus()
	}, [open])
	const choose = (id: string | null) => {
		setOpen(false)
		onChoose(id)
	}
	if (stages.length === 0) return null
	const selected = stages.find((stage) => stage.id === selectedId) ?? null

	return (
		<>
			{/* A la derecha quedan libres los controles de MapLibre (right-16). La fila no capta
			    toques en su hueco vacío: solo sus botones. */}
			<div className="pointer-events-none absolute top-3 right-16 left-3 z-10 flex items-center gap-2">
				{selected ? (
					<button
						type="button"
						aria-expanded={open}
						aria-label={t('map.changeStage', { n: selected.order, name: selected.name })}
						onClick={() => setOpen(true)}
						className="pointer-events-auto inline-flex min-h-12 min-w-0 items-center gap-2 rounded-full bg-forest-700 py-1.5 pr-4 pl-1.5 font-semibold text-white shadow-md active:brightness-90"
					>
						<span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-white font-bold text-forest-800">
							{selected.order}
						</span>
						<span className="truncate">{selected.name}</span>
					</button>
				) : (
					<button
						type="button"
						aria-expanded={open}
						onClick={() => setOpen(true)}
						className="pointer-events-auto inline-flex min-h-12 items-center gap-2 rounded-full bg-white py-2 pr-5 pl-4 font-semibold text-forest-800 shadow-md active:bg-earth-100"
					>
						<Icon name="route" />
						{t('map.stages')}
					</button>
				)}
				{selected && (
					<>
						<div className="pointer-events-auto contents">{children}</div>
						<button
							type="button"
							onClick={() => onChoose(null)}
							aria-label={t('map.wholeWay')}
							className="pointer-events-auto flex size-12 shrink-0 items-center justify-center rounded-full bg-white text-earth-800 shadow-md active:bg-earth-100"
						>
							<Icon name="close" />
						</button>
					</>
				)}
			</div>
			{open && (
				<section
					role="dialog"
					aria-modal="true"
					aria-label={t('map.chooseStage')}
					onKeyDown={(event) => event.key === 'Escape' && setOpen(false)}
					className="absolute inset-2 z-20 mx-auto flex max-w-xl flex-col rounded-3xl bg-white shadow-xl"
				>
					<header className="flex items-center gap-2 border-b border-earth-100 py-2 pr-2 pl-5">
						<h2 className="flex-1 text-2xl font-semibold">{t('map.chooseStage')}</h2>
						<button
							type="button"
							onClick={() => setOpen(false)}
							aria-label={t('map.close')}
							ref={closeButton}
							className="flex size-12 items-center justify-center rounded-full text-earth-800"
						>
							<Icon name="close" />
						</button>
					</header>
					<ol className="flex-1 overflow-y-auto p-2">
						<li>
							<button
								type="button"
								onClick={() => choose(null)}
								aria-current={selectedId === null ? 'true' : undefined}
								className={`${ROW} ${selectedId === null ? 'bg-sage-50' : ''}`}
							>
								<span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-olive-900 text-white">
									<Icon name="map" className="size-5" />
								</span>
								<span className="flex-1 font-bold text-forest-800">{t('map.wholeWay')}</span>
							</button>
						</li>
						{stages.map((stage) => {
							const current = stage.id === selectedId
							return (
								<li key={stage.id}>
									<button
										type="button"
										onClick={() => choose(stage.id)}
										aria-current={current ? 'true' : undefined}
										className={`${ROW} ${current ? 'bg-sage-50' : ''}`}
									>
										<span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-sage-200 font-bold text-olive-900">
											{stage.order}
										</span>
										<span className="min-w-0 flex-1 font-semibold">{stage.name}</span>
										<span className="shrink-0 text-earth-800">
											{t('stages.distance', { km: formatKm(stage.distanceKm, locale) })}
										</span>
									</button>
								</li>
							)
						})}
					</ol>
				</section>
			)}
		</>
	)
}
